import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, Rocket, Menu, X, Sparkles, Lock } from 'lucide-react';
import PremiumDarkAtmosphere from '../components/PremiumDarkAtmosphere';
import OffersQuickAccess from '../components/OffersQuickAccess';
import SabrilCloserBubble from '../components/SabrilCloserBubble';
import Resto360Wordmark from '../components/Resto360Wordmark';

const TESTIMONIALS = [
  {
    id: 'philippe',
    initial: 'P',
    name: 'Philippe',
    role: 'Multi-restaurateur',
    location: '🇲🇦 Marrakech',
    tag: 'Ouverture',
    quote: (
      <>
        « Sabril s'est occupé de l'<strong className="not-italic text-white">ouverture de A à Z</strong> pour mon resto en plein centre de Marrakech. Un travail de grand professionnel — j'ai aujourd'hui une <strong className="not-italic text-white">marque forte</strong>, un <strong className="not-italic text-white">restaurant pilotable</strong>, et surtout <strong className="not-italic text-[#F05A1A]">je gagne de l'argent</strong>. »
      </>
    ),
  },
  {
    id: 'karim',
    initial: 'K',
    name: 'Karim',
    role: 'Brasserie urbaine',
    location: '🇫🇷 Lyon',
    tag: 'Ouverture',
    quote: (
      <>
        « J'avais le local, le budget, le rêve — mais zéro méthode. Sabril a <strong className="not-italic text-white">structuré le concept, l'équipe et la carte</strong> avant même qu'on ouvre. Résultat : <strong className="not-italic text-[#F05A1A]">complet dès la 3ème semaine</strong>, et une rentabilité que je n'osais pas espérer. »
      </>
    ),
  },
  {
    id: 'elodie',
    initial: 'É',
    name: 'Élodie',
    role: 'Bistrot de quartier',
    location: '🇫🇷 Bordeaux',
    tag: 'Refonte',
    quote: (
      <>
        « Mon resto perdait 4 000€/mois, j'allais fermer. En <strong className="not-italic text-white">90 jours de refonte</strong> avec Sabril, on a refait la carte, viré les postes qui plombaient, retravaillé l'expérience. Aujourd'hui je suis <strong className="not-italic text-[#F05A1A]">+18% de marge</strong> et je dors la nuit. »
      </>
    ),
  },
  {
    id: 'thomas',
    initial: 'T',
    name: 'Thomas',
    role: 'Restaurant gastronomique',
    location: '🇫🇷 Paris',
    tag: '1 Day',
    quote: (
      <>
        « Une seule journée avec Sabril et c'est comme si on m'avait <strong className="not-italic text-white">enlevé un voile</strong>. Il a vu en 4h ce que mon comptable n'avait pas vu en 3 ans. Repartis avec un plan d'action <strong className="not-italic text-[#F05A1A]">précis, chiffré, exécutable lundi matin</strong>. »
      </>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────
// BrandsSignedSection — iOS App Icon-style card list of brands
// White rounded card · colored square icon (initials) · title · tagline · meta
// ─────────────────────────────────────────────────────────────────────
const BRANDS_SIGNED = [
  {
    key: 'maison-avocado',
    initials: 'MA',
    name: 'Maison Avocado',
    tagline: 'L\'invitation au voyage',
    city: 'Lille · Marrakech',
    year: '2018 → 2026',
    overline: 'FRANCHISE · N°01',
    iconBg: 'linear-gradient(135deg, #3CB371 0%, #1A5D3F 100%)',
    iconGlow: 'rgba(60,179,113,0.35)',
    accent: '#3CB371',
  },
  {
    key: 'palazzo',
    initials: 'PZ',
    name: 'Palazzo',
    tagline: 'Trattoria signature',
    city: 'Paris · Marrakech',
    year: '2024',
    overline: 'ITALIAN MODERN · N°02',
    iconBg: 'linear-gradient(135deg, #C9A87A 0%, #8B6520 100%)',
    iconGlow: 'rgba(201,168,122,0.40)',
    accent: '#B8934E',
  },
  {
    key: 'zebra-gang',
    initials: 'ZG',
    name: 'Zebra Gang',
    tagline: 'Pure Italian Gang',
    city: 'Naples × Marrakech',
    year: '2023',
    overline: 'PIZZA · STREETWEAR · N°03',
    iconBg: 'linear-gradient(135deg, #1C1C1E 0%, #000000 100%)',
    iconGlow: 'rgba(255,59,48,0.45)',
    accent: '#FF3B30',
  },
  {
    key: 'bensimon',
    initials: 'CB',
    name: 'Camille Bensimon',
    tagline: 'Direction de marque premium',
    city: 'Paris',
    year: '2024',
    overline: 'VESTIAIRE · N°04',
    iconBg: 'linear-gradient(135deg, #7B89A5 0%, #3D4858 100%)',
    iconGlow: 'rgba(123,137,165,0.40)',
    accent: '#7B89A5',
  },
  {
    key: 'bon',
    initials: 'BN',
    name: 'BON',
    tagline: 'In Msemen we trust',
    city: 'Marrakech',
    year: '2022',
    overline: 'CAFÉ · BRUNCH PREMIUM · N°05',
    iconBg: 'linear-gradient(135deg, #E5C68A 0%, #9C7536 100%)',
    iconGlow: 'rgba(229,198,138,0.45)',
    accent: '#C9A87A',
  },
];

function BrandsSignedSection() {
  return (
    <section className="mt-12" data-testid="brands-signed-section">
      {/* Section header */}
      <div className="mb-6">
        <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#F05A1A]">
          Portfolio · marques signées
        </p>
        <h2 className="mt-2 font-['Playfair_Display',serif] text-[30px] sm:text-[36px] font-black leading-[0.98] tracking-[-0.025em] text-[#1a1a1a]">
          Les marques que <span className="italic">j'ai bâties</span>.
        </h2>
        <p className="mt-3 text-[14px] text-[#1a1a1a]/60 leading-snug max-w-md">
          Pas des logos posés sur un site — des marques construites de A à Z. ADN, produit, expérience, système, rentabilité.
        </p>
      </div>

      {/* iOS-style card list */}
      <div className="space-y-3">
        {BRANDS_SIGNED.map((b, i) => (
          <motion.div
            key={b.key}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 + i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[20px] bg-white overflow-hidden"
            style={{
              boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -12px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.05)',
            }}
            data-testid={`brand-card-${b.key}`}
          >
            <div className="flex items-center gap-4 p-4">
              {/* iOS App Icon */}
              <div className="relative flex-shrink-0">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 62,
                    height: 62,
                    borderRadius: 16,
                    background: b.iconBg,
                    boxShadow: `0 10px 24px -8px ${b.iconGlow}, inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.15)`,
                  }}
                >
                  <span
                    className="font-black text-white"
                    style={{
                      fontFamily: '"Playfair Display", "Times New Roman", serif',
                      fontSize: 22,
                      letterSpacing: '-0.02em',
                      textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                    }}
                  >
                    {b.initials}
                  </span>
                </div>
                {/* Number badge */}
                <span
                  className="absolute -top-1 -right-1 flex items-center justify-center text-white font-black rounded-full"
                  style={{
                    width: 20,
                    height: 20,
                    background: b.accent,
                    fontSize: 10,
                    fontFamily: '"Space Mono", monospace',
                    letterSpacing: '-0.02em',
                    boxShadow: `0 4px 10px ${b.iconGlow}, 0 0 0 2px #FFFFFF`,
                  }}
                >
                  {i + 1}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p
                  style={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: 9.5,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    color: b.accent,
                    marginBottom: 3,
                  }}
                >
                  {b.overline}
                </p>
                <p
                  className="truncate"
                  style={{
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                    fontSize: 17,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: '#1a1a1a',
                    lineHeight: 1.15,
                  }}
                >
                  {b.name}
                </p>
                <p
                  style={{
                    fontSize: 12.5,
                    color: '#1a1a1a',
                    opacity: 0.55,
                    lineHeight: 1.35,
                    fontStyle: 'italic',
                    fontFamily: 'Georgia, serif',
                    marginTop: 2,
                  }}
                >
                  « {b.tagline} »
                </p>
              </div>

              {/* Chevron */}
              <svg
                className="flex-shrink-0"
                width="8"
                height="14"
                viewBox="0 0 8 14"
                fill="none"
                style={{ color: '#C7C7CC' }}
                aria-hidden
              >
                <path
                  d="M1 1L7 7L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Bottom meta bar */}
            <div
              className="flex items-center justify-between px-4 pb-3 pt-1"
              style={{ borderTop: '0.5px solid rgba(0,0,0,0.06)', paddingTop: 10 }}
            >
              <p
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: '#1a1a1a',
                  opacity: 0.75,
                  letterSpacing: '-0.01em',
                }}
              >
                📍 {b.city}
              </p>
              <p
                style={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#1a1a1a',
                  opacity: 0.45,
                  letterSpacing: '0.06em',
                }}
              >
                {b.year}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Footer credentials */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-[11px] text-[#1a1a1a]/45 font-medium">
          <span className="text-[#F05A1A] font-black">180+</span> restos audités · 5 marques bâties de zéro
        </p>
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <span key={i} className="text-xs text-[#F05A1A]">★</span>
          ))}
        </div>
      </div>
    </section>
  );
}

const HomePage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const sabrilPhoto = "https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/6ozjn0ci_Story%20Espace%20Professionnel%20-%20%20Instagram%20%28Your%20Story%29.png.jpg";

  // Auto-rotate testimonials every 6s
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleStart = () => {
    navigate('/diagnostic');
  };

  const t = TESTIMONIALS[currentTestimonial];

  const menuItems = [
    { icon: Rocket, label: 'Le Salon Privé', path: '/prsnl', color: '#0B2B24' },
    { icon: BookOpen, label: 'Sabril Express · 790€', path: '/offre/ebook', color: '#D89440' },
  ];

  return (
    <div
      className="relative min-h-[100svh] flex flex-col overflow-hidden"
      style={{ background: '#f5f3f0' }}
      data-testid="home-page"
    >
      {/* Background atmosphère subtile retirée — bg clair pour ressortir la photo et le texte */}

      {/* Menu Hamburger Button - Top Left */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        onClick={() => setMenuOpen(true)}
        className="fixed top-4 left-4 z-[50] w-12 h-12 rounded-xl flex items-center justify-center transition-all bg-[#1a1a1a]/[0.08] hover:bg-[#1a1a1a]/[0.12] border border-[#1a1a1a]/15 backdrop-blur-sm"
        data-testid="menu-hamburger"
      >
        <Menu className="w-6 h-6 text-[#1a1a1a]" />
      </motion.button>

      {/* Menu Overlay */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            />

            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 left-0 bottom-0 w-[280px] z-[70] flex flex-col border-r border-white/10"
              style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)' }}
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <span className="font-bebas text-2xl tracking-[4px] text-[#F05A1A]">PRSNL</span>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/[0.06] border border-white/15"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Menu Items */}
              <nav className="flex-1 p-4">
                <ul className="space-y-2">
                  {menuItems.map((item, index) => (
                    <motion.li
                      key={item.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-4 p-4 rounded-xl transition-all group hover:bg-white/[0.04]"
                        data-testid={`menu-item-${item.path.replace('/', '') || 'home'}`}
                      >
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${item.color}25` }}
                        >
                          <item.icon className="w-5 h-5" style={{ color: item.color }} />
                        </div>
                        <span className="flex-1 font-semibold transition-colors flex items-center gap-2 text-white">
                          {item.path === '/app' ? (
                            <Resto360Wordmark variant="sidebar" tone="dark" />
                          ) : (
                            item.label
                          )}
                          {item.locked && <Lock className="w-3.5 h-3.5 text-white/40" />}
                        </span>
                        {item.badge && (
                          <span
                            className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                            style={{ backgroundColor: `${item.color}25`, color: item.color }}
                          >
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </nav>

              {/* Menu Footer */}
              <div className="p-5 border-t border-white/10">
                <p className="text-xs text-center text-white/35">© 2025 PRSNL</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* === HERO PHOTO — DESKTOP ONLY (right side, full bleed) === */}
      <img
        src={sabrilPhoto}
        alt="Sabril"
        className="hidden sm:block absolute right-0 w-auto object-contain z-[1] sm:h-[115%] sm:top-[-10%] max-w-[70%]"
        style={{
          filter: 'brightness(0.95) contrast(1.08) saturate(1.05)',
          right: '-2%',
        }}
        data-testid="hero-photo"
      />

      {/* Light gradient overlay — DESKTOP only (diagonal fade for text readability) */}
      <div
        className="absolute inset-0 z-[2] hidden sm:block"
        style={{
          background:
            'linear-gradient(100deg, rgba(245,243,240,1) 0%, rgba(245,243,240,0.98) 38%, rgba(245,243,240,0.65) 55%, transparent 75%)',
        }}
      />

      {/* Bottom soft gradient (desktop) */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[250px] z-[3] hidden sm:block"
        style={{
          background:
            'linear-gradient(to top, rgba(245,243,240,1) 0%, rgba(245,243,240,0.98) 40%, rgba(245,243,240,0.6) 70%, transparent 100%)',
        }}
      />

      {/* Sabril Badge - Floating right side (dark glass-morphism sur bg clair) - desktop only */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="hidden sm:flex absolute top-20 right-5 z-[5] rounded-full py-2 px-3.5 pr-4 items-center gap-2.5 bg-[#1a1a1a] backdrop-blur-md border border-[#1a1a1a]/20 shadow-[0_8px_24px_rgba(0,0,0,0.18)]"
      >
        <div className="w-[34px] h-[34px] rounded-full flex items-center justify-center font-black text-sm text-white flex-shrink-0 bg-[#F05A1A] shadow-[0_0_20px_rgba(240,90,26,0.6)]">
          S
        </div>
        <div className="leading-tight">
          <div className="text-[13px] font-extrabold text-white">Sabril</div>
          <div className="text-[11px] text-white/55">@Sabril.j</div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="relative z-[4] pt-20 sm:pt-36 px-5 sm:px-7 flex-1 flex flex-col max-w-full sm:max-w-[60%]">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 w-fit mb-5 sm:mb-8 bg-[#F05A1A]/15 border border-[#F05A1A]/40 backdrop-blur-sm"
        >
          <span className="w-[7px] h-[7px] rounded-full animate-pulse bg-[#F05A1A]"></span>
          <span className="text-xs font-bold tracking-wide uppercase text-[#F05A1A]">
            Diagnostic gratuit
          </span>
        </motion.div>

        {/* Main Title — tighter on mobile to fit 3 lines */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="font-bebas leading-[0.88] tracking-wide mb-4 sm:mb-5 text-[#1a1a1a]"
          style={{ fontSize: 'clamp(54px, 13.5vw, 92px)' }}
        >
          <span className="block">ARRÊTE DE</span>
          <span
            className="block"
            style={{
              background: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 50%, #F05A1A 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            BOSSER
          </span>
          <span className="block">POUR RIEN.</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-[15px] sm:text-base leading-relaxed font-medium mb-5 sm:mb-7 text-[#1a1a1a]/65"
        >
          Diagnostic ultra-rapide.<br />
          La vérité sur ton resto.
        </motion.p>

        {/* === EDITORIAL PHOTO CARD — MOBILE ONLY === */}
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.45, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="sm:hidden relative rounded-3xl overflow-hidden mb-5"
          data-testid="hero-photo-card-mobile"
          style={{
            background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.18), 0 0 0 1px rgba(240,90,26,0.18) inset',
            aspectRatio: '4 / 5',
          }}
        >
          {/* Photo — centered on face/torso */}
          <img
            src={sabrilPhoto}
            alt="Sabril, ton coach restaurant"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: '50% 35%', filter: 'brightness(0.98) contrast(1.10) saturate(1.06)' }}
          />

          {/* Glow halo top-right */}
          <div
            className="absolute -top-12 -right-12 w-48 h-48 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(240,90,26,0.45) 0%, transparent 70%)' }}
          />

          {/* Top dark gradient for badge readability */}
          <div
            className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 100%)' }}
          />

          {/* Sabril badge top-left */}
          <div className="absolute top-3.5 left-3.5 flex items-center gap-2 rounded-full py-1.5 px-2.5 pr-3.5 bg-black/55 backdrop-blur-md border border-white/15">
            <div className="w-7 h-7 rounded-full flex items-center justify-center font-black text-[11px] text-white bg-[#F05A1A] shadow-[0_0_12px_rgba(240,90,26,0.7)]">
              S
            </div>
            <div className="leading-tight">
              <div className="text-[11px] font-extrabold text-white">Sabril</div>
              <div className="text-[9px] text-white/65">@Sabril.j</div>
            </div>
          </div>

          {/* Live status dot top-right */}
          <div className="absolute top-3.5 right-3.5 flex items-center gap-1.5 rounded-full py-1 px-2.5 bg-emerald-500/20 backdrop-blur-md border border-emerald-400/35">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-emerald-400"
              animate={{ opacity: [1, 0.35, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            />
            <span className="text-[9px] font-black uppercase tracking-wider text-emerald-300">En ligne</span>
          </div>

          {/* Bottom dark gradient for stats readability */}
          <div
            className="absolute bottom-0 left-0 right-0 h-44 pointer-events-none"
            style={{ background: 'linear-gradient(0deg, rgba(15,5,5,0.95) 0%, rgba(15,5,5,0.55) 50%, transparent 100%)' }}
          />

          {/* Floating stat sticker — repositioned BOTTOM-RIGHT to avoid covering the face */}
          <motion.div
            initial={{ scale: 0, rotate: -8 }}
            animate={{ scale: 1, rotate: -6 }}
            transition={{ delay: 0.9, type: 'spring', stiffness: 220, damping: 14 }}
            className="absolute bottom-28 right-3.5 rounded-2xl px-3 py-2 flex flex-col items-center z-10"
            style={{
              background: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 100%)',
              boxShadow: '0 12px 30px rgba(240,90,26,0.55), 0 0 0 2px rgba(255,255,255,0.18) inset',
            }}
          >
            <span className="font-bebas text-white leading-none" style={{ fontSize: 22 }}>180+</span>
            <span className="text-white/90 text-[9px] font-bold uppercase tracking-wider mt-0.5">restos audités</span>
          </motion.div>

          {/* Bottom stats row */}
          <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
            <div className="grid grid-cols-3 gap-2.5">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="rounded-xl px-2 py-2 text-center bg-white/8 backdrop-blur-md border border-white/12"
              >
                <div className="font-bebas text-white text-xl leading-none">+18%</div>
                <div className="text-white/65 text-[8.5px] font-bold uppercase tracking-wider mt-1">marge moy.</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="rounded-xl px-2 py-2 text-center bg-white/8 backdrop-blur-md border border-white/12"
              >
                <div className="font-bebas text-white text-xl leading-none">3 min</div>
                <div className="text-white/65 text-[8.5px] font-bold uppercase tracking-wider mt-1">diagnostic</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="rounded-xl px-2 py-2 text-center bg-white/8 backdrop-blur-md border border-white/12"
              >
                <div className="font-bebas text-[#FBBF24] text-xl leading-none">★ 5/5</div>
                <div className="text-white/65 text-[8.5px] font-bold uppercase tracking-wider mt-1">vérifié</div>
              </motion.div>
            </div>

            {/* Credentials line */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.05 }}
              className="text-white/65 text-[10px] font-medium text-center mt-2.5"
            >
              Fondateur Maison Avocado · Bring Café
            </motion.p>
          </div>
        </motion.div>

        {/* Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex gap-5 flex-wrap"
        >
          <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1a1a1a]/70">
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-[#F05A1A]" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            3 min
          </div>
          <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1a1a1a]/70">
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-[#F05A1A]" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            100% confidentiel
          </div>
        </motion.div>

        {/* === QUICK ACCESS OFFERS — 3 capsules iOS-style (mobile only sous la photo) === */}
        <div className="sm:hidden mt-6">
          <OffersQuickAccess
            variant="light"
            kicker="🎯 3 options pour passer à l'action"
            title="Choisis ton format."
            hideCurrentPage={false}
            className="!my-0"
          />
        </div>

        {/* TESTIMONIAL CARD — Removed on all devices (user request) */}
      </div>

      {/* CTA Section - Bottom */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="relative z-[5] px-5 mt-auto"
      >
        <button
          onClick={handleStart}
          data-testid="start-diagnostic-btn"
          className="w-full text-white font-black text-[15px] tracking-[1.5px] uppercase py-5 px-7 rounded-2xl transition-all duration-300 flex items-center justify-center gap-2.5 hover:-translate-y-0.5 bg-[#F05A1A] hover:bg-[#FF6A2A]"
          style={{
            boxShadow: '0 8px 32px rgba(240,90,26,0.5), 0 0 0 1px rgba(240,90,26,0.3) inset',
          }}
        >
          DÉMARRER LE DIAGNOSTIC
          <span className="text-lg">→</span>
        </button>
      </motion.div>

      {/* Footer with 3 lines */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="relative z-[5] px-5 pt-6 pb-10"
      >
        <div className="pt-5 border-t border-[#1a1a1a]/10">
          <div className="flex flex-col items-center gap-2 text-center">
            {/* Line 1 - Credentials */}
            <p className="text-xs font-medium text-[#1a1a1a]/70">
              Fondateur Maison Avocado · Bring Café
            </p>

            {/* Line 2 - Experience */}
            <p className="text-xs text-[#1a1a1a]/45">
              Ex publicitaire BFM · RMC · PSG
            </p>

            {/* Line 3 - Stars + Copyright */}
            <div className="flex items-center gap-3 mt-1">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-sm text-[#F05A1A]">★</span>
                ))}
              </div>
              <span className="text-[10px] text-[#1a1a1a]/35">© 2025 PRSNL</span>
            </div>
          </div>
        </div>
      </motion.footer>
      <SabrilCloserBubble page="home" />
    </div>
  );
};

export default HomePage;
